import React, { useState, useEffect } from 'react';
import { Box, Typography, styled, ToggleButtonGroup, ToggleButton, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, FormControl, InputLabel, Select, Switch, FormControlLabel } from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as InProgressIcon,
  Upcoming as UpcomingIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  ViewDay as DayViewIcon,
  ViewWeek as WeekViewIcon,
  ViewModule as MonthViewIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  parseISO,
  isSameMonth,
  isToday,
  addMonths,
  subMonths,
  set
} from 'date-fns';
import EventTimelineView from './EventTimelineView';
import TimelineGanttView from './TimelineGanttView';
import DayView from './DayView';
import { eventTypeGradients, programTypeGradients } from './styles/eventStyles';

const StyledCalendarWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(18, 18, 18, 0.95)',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  minHeight: 'calc(100vh - 300px)'
}));

const CalendarGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: '1px',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  padding: '1px',
  borderRadius: theme.spacing(1),
}));

const CalendarCell = styled(Box)(({ isCurrentMonth = true, isToday = false }) => ({
  backgroundColor: 'rgba(18, 18, 18, 0.95)',
  padding: '8px',
  minHeight: '120px',
  position: 'relative',
  cursor: 'pointer',
  opacity: isCurrentMonth ? 1 : 0.5,
  border: isToday ? '1px solid #7C4DFF' : 'none',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  }
}));

const StatsContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3)
}));

const StatBox = styled(Box)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.05)',
  borderRadius: theme.spacing(1),
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1)
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
    transform: 'translate(14px, -12px) scale(0.75)',
    '&.Mui-focused': {
      color: '#7C4DFF',
    },
  },
  '& .MuiInputBase-root': {
    marginTop: '16px',
    '& input, & textarea': {
      color: '#fff',
    },
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.23)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#7C4DFF',
    },
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
    '&.Mui-focused': {
      color: '#7C4DFF',
    },
  },
  '& .MuiSelect-root': {
    color: '#fff',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.23)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#7C4DFF',
    },
  },
}));

const ViewToggleButtons = styled(ToggleButtonGroup)(({ theme }) => ({
  backgroundColor: 'rgba(18, 18, 18, 0.95)',
  '& .MuiToggleButton-root': {
    color: 'rgba(255, 255, 255, 0.7)',
    padding: '8px 16px',
    '&.Mui-selected': {
      color: '#7C4DFF',
      backgroundColor: 'rgba(124, 77, 255, 0.1)',
    },
    '&:hover': {
      backgroundColor: 'rgba(124, 77, 255, 0.05)',
    },
  },
}));

const EventDialog = ({ 
  open, 
  onClose, 
  selectedDate, 
  selectedEvent, 
  onSave, 
  onDelete 
}) => {
  const defaultStartTime = '09:00';
  const defaultEndTime = '17:00';
  
  const [formData, setFormData] = useState({
    title: selectedEvent?.title || '',
    description: selectedEvent?.description || '',
    type: selectedEvent?.type || 'program',
    programType: selectedEvent?.programType || '',
    startTime: selectedEvent?.startTime || defaultStartTime,
    endTime: selectedEvent?.endTime || defaultEndTime,
    location: selectedEvent?.location || '',
    virtual: selectedEvent?.virtual || false,
    meetingLink: selectedEvent?.meetingLink || ''
  });

  useEffect(() => {
    if (selectedDate) {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      setFormData(prev => ({
        ...prev,
        date: dateStr,
        startTime: selectedEvent?.startTime || defaultStartTime,
        endTime: selectedEvent?.endTime || defaultEndTime
      }));
    }
  }, [selectedDate, selectedEvent]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const startDateTime = `${formData.date}T${formData.startTime}`;
    const endDateTime = `${formData.date}T${formData.endTime}`;
    
    onSave({
      ...selectedEvent,
      ...formData,
      startDate: startDateTime,
      endDate: endDateTime,
      id: selectedEvent?.id || Date.now()
    });
    onClose();
  };

  const programTypes = {
    program: [
      'Workshop',
      'Training',
      'Community Event',
      'Educational Series',
      'Cultural Program',
      'Youth Program',
      'Senior Program',
      'Health & Wellness',
      'Arts & Culture',
      'Environmental',
      'Social Justice',
      'Other'
    ],
    fundraising: [
      'Gala',
      'Silent Auction',
      'Benefit Concert',
      'Donor Reception',
      'Crowdfunding Event',
      'Annual Campaign',
      'Charity Drive',
      'Sponsorship Event',
      'Other'
    ],
    administrative: [
      'Board Meeting',
      'Staff Meeting',
      'Strategic Planning',
      'Budget Review',
      'Grant Review',
      'Compliance Review',
      'Other'
    ]
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: 'rgba(18, 18, 18, 0.95)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }
      }}
    >
      <DialogTitle sx={{ color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {selectedEvent ? 'Edit Event' : 'New Event'}
        <IconButton onClick={onClose} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <StyledTextField
              label="Event Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              fullWidth
            />
            
            <StyledTextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />

            <StyledFormControl fullWidth>
              <InputLabel>Event Type</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value, programType: '' })}
              >
                <MenuItem value="program">Program Event</MenuItem>
                <MenuItem value="fundraising">Fundraising Event</MenuItem>
                <MenuItem value="administrative">Administrative Event</MenuItem>
              </Select>
            </StyledFormControl>

            <StyledFormControl fullWidth>
              <InputLabel>Program Type</InputLabel>
              <Select
                value={formData.programType}
                onChange={(e) => setFormData({ ...formData, programType: e.target.value })}
              >
                {programTypes[formData.type]?.map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </StyledFormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <StyledTextField
                label="Start Time"
                type="time"
                value={formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                required
                InputLabelProps={{ shrink: true }}
              />
              <StyledTextField
                label="End Time"
                type="time"
                value={formData.endTime}
                onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <StyledTextField
              label="Location"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              fullWidth
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.virtual}
                  onChange={(e) => setFormData({ ...formData, virtual: e.target.checked })}
                />
              }
              label="Virtual Event"
              sx={{ color: '#fff' }}
            />

            {formData.virtual && (
              <StyledTextField
                label="Meeting Link"
                value={formData.meetingLink}
                onChange={(e) => setFormData({ ...formData, meetingLink: e.target.value })}
                fullWidth
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          {selectedEvent && (
            <Button
              onClick={() => {
                onDelete(selectedEvent.id);
                onClose();
              }}
              color="error"
              startIcon={<DeleteIcon />}
            >
              Delete
            </Button>
          )}
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" color="primary">
            {selectedEvent ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const MonthView = ({ events, currentDate, onEventClick, onDateClick }) => {
  const startDate = startOfMonth(currentDate);
  const endDate = endOfMonth(currentDate);
  const startWeek = startOfWeek(startDate);
  const endWeek = endOfWeek(endDate);
  const days = eachDayOfInterval({ start: startWeek, end: endWeek });

  return (
    <Box>
      <CalendarGrid>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <Box
            key={day}
            sx={{
              backgroundColor: 'rgba(18, 18, 18, 0.95)',
              padding: '8px',
              textAlign: 'center',
              color: '#fff',
              fontWeight: 500
            }}
          >
            {day}
          </Box>
        ))}
        {days.map((day) => (
          <CalendarCell
            key={day.toString()}
            onClick={() => onDateClick(day)}
            isCurrentMonth={isSameMonth(day, currentDate)}
            isToday={isToday(day)}
          >
            <Typography
              sx={{
                color: isToday(day) ? '#7C4DFF' : '#fff',
                fontWeight: isToday(day) ? 'bold' : 'normal'
              }}
            >
              {format(day, 'd')}
            </Typography>
            <Box sx={{ mt: 1 }}>
              {events
                .filter(event => format(parseISO(event.startDate), 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd'))
                .map((event) => (
                  <Box
                    key={event.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEventClick(event);
                    }}
                    sx={{
                      p: 0.5,
                      mb: 0.5,
                      borderRadius: '4px',
                      background: eventTypeGradients[event.type].gradient,
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      color: '#fff',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        transition: 'transform 0.2s'
                      }
                    }}
                  >
                    {event.title}
                  </Box>
                ))}
            </Box>
          </CalendarCell>
        ))}
      </CalendarGrid>
    </Box>
  );
};

const TimelineView = ({ events, onEventClick }) => {
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        minWidth: '800px'
      }}>
        {events.map((event) => (
          <Box
            key={event.id}
            onClick={() => onEventClick(event)}
            sx={{
              p: 2,
              borderRadius: 1,
              background: eventTypeGradients[event.type].gradient,
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              '&:hover': {
                transform: 'translateY(-2px)',
                transition: 'transform 0.2s'
              }
            }}
          >
            <Box>
              <Typography sx={{ color: '#fff', fontWeight: 'bold' }}>
                {event.title}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {format(parseISO(event.startDate), 'MMM d, yyyy HH:mm')}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              {event.type}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const EventCalendarView = ({ events = [], onAddEvent, onUpdateEvent, onDeleteEvent }) => {
  const [view, setView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setDialogOpen(true);
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setSelectedEvent(null);
    setDialogOpen(true);
  };

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setView(newView);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedEvent(null);
    setSelectedDate(null);
  };

  const handleSaveEvent = (eventData) => {
    if (selectedEvent) {
      onUpdateEvent(eventData);
    } else {
      onAddEvent(eventData);
    }
    handleDialogClose();
  };

  const handleDeleteEvent = (eventId) => {
    onDeleteEvent(eventId);
    handleDialogClose();
  };

  const renderContent = () => {
    switch (view) {
      case 'day':
        return (
          <DayView 
            events={events}
            currentDate={currentDate}
            onEventClick={handleEventClick}
          />
        );
      case 'week':
        return (
          <WeekView 
            events={events}
            currentDate={currentDate}
            onEventClick={handleEventClick}
          />
        );
      case 'timeline':
        return (
          <TimelineGanttView 
            events={events}
            onEventClick={handleEventClick}
          />
        );
      case 'month':
      default:
        return (
          <MonthView 
            events={events}
            currentDate={currentDate}
            onEventClick={handleEventClick}
            onDateClick={handleDateClick}
          />
        );
    }
  };

  return (
    <Box>
      {/* Header with view toggles */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3
      }}>
        <Typography variant="h5" sx={{ color: '#fff' }}>
          Event Schedule
        </Typography>
        <ViewToggleButtons
          value={view}
          exclusive
          onChange={handleViewChange}
          aria-label="view selector"
        >
          <ToggleButton value="day" aria-label="day view">
            <DayViewIcon />
          </ToggleButton>
          <ToggleButton value="week" aria-label="week view">
            <WeekViewIcon />
          </ToggleButton>
          <ToggleButton value="month" aria-label="month view">
            <MonthViewIcon />
          </ToggleButton>
          <ToggleButton value="timeline" aria-label="timeline view">
            <TimelineIcon />
          </ToggleButton>
        </ViewToggleButtons>
      </Box>

      <StyledCalendarWrapper>
        {renderContent()}
      </StyledCalendarWrapper>

      {/* Event Dialog */}
      <EventDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        selectedDate={selectedDate}
        selectedEvent={selectedEvent}
        onSave={handleSaveEvent}
        onDelete={handleDeleteEvent}
      />
    </Box>
  );
};

const WeekView = ({ events, currentDate, onEventClick }) => {
  const startDate = startOfWeek(currentDate);
  const endDate = endOfWeek(currentDate);
  const weekDays = eachDayOfInterval({ start: startDate, end: endDate });

  return (
    <StyledCalendarWrapper>
      {/* Week Header */}
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: '80px repeat(7, 1fr)',
        position: 'sticky',
        top: 0,
        zIndex: 2,
        backgroundColor: 'rgba(18, 18, 18, 0.95)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        mb: 1
      }}>
        <Box /> {/* Empty cell for time column */}
        {weekDays.map(day => (
          <Box
            key={day.toString()}
            sx={{
              p: 2,
              textAlign: 'center',
              borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
              background: isToday(day) 
                ? 'linear-gradient(180deg, rgba(0, 188, 212, 0.1) 0%, rgba(32, 178, 170, 0.1) 100%)'
                : 'transparent'
            }}
          >
            <Typography sx={{ 
              color: isToday(day) ? '#00BCD4' : '#fff',
              fontWeight: 500 
            }}>
              {format(day, 'EEE')}
            </Typography>
            <Typography sx={{ 
              color: isToday(day) ? '#00BCD4' : 'rgba(255, 255, 255, 0.7)',
              fontSize: '0.875rem'
            }}>
              {format(day, 'd')}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Time Grid */}
      <Box sx={{ 
        height: 'calc(100vh - 460px)', 
        overflowY: 'auto',
        position: 'relative'
      }}>
        {Array.from({ length: 24 }).map((_, hour) => (
          <Box
            key={hour}
            sx={{
              display: 'grid',
              gridTemplateColumns: '80px repeat(7, 1fr)',
              minHeight: '60px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.02)'
              }
            }}
          >
            {/* Time Label */}
            <Box sx={{
              p: 1,
              borderRight: '1px solid rgba(255, 255, 255, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Typography sx={{ 
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.75rem'
              }}>
                {format(set(new Date(), { hours: hour, minutes: 0 }), 'HH:mm')}
              </Typography>
            </Box>

            {/* Day Columns */}
            {weekDays.map(day => {
              const currentSlotEvents = events.filter(event => {
                const eventDate = parseISO(event.startDate);
                return (
                  format(eventDate, 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd') &&
                  eventDate.getHours() === hour
                );
              });

              return (
                <Box
                  key={day.toString()}
                  sx={{
                    borderLeft: '1px solid rgba(255, 255, 255, 0.05)',
                    p: 0.5,
                    position: 'relative',
                    minHeight: '60px'
                  }}
                >
                  {currentSlotEvents.map(event => (
                    <Box
                      key={event.id}
                      onClick={() => onEventClick(event)}
                      sx={{
                        position: 'absolute',
                        left: '4px',
                        right: '4px',
                        minHeight: '28px',
                        background: eventTypeGradients[event.type]?.gradient || 'rgba(124, 77, 255, 0.2)',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        cursor: 'pointer',
                        zIndex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'scale(1.02)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                        }
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#fff',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {event.title}
                      </Typography>
                      <Typography
                        sx={{
                          color: 'rgba(255, 255, 255, 0.7)',
                          fontSize: '0.7rem'
                        }}
                      >
                        {format(parseISO(event.startDate), 'HH:mm')}
                        {event.endDate && ` - ${format(parseISO(event.endDate), 'HH:mm')}`}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              );
            })}
          </Box>
        ))}

        {/* Current Time Indicator */}
        {isToday(new Date()) && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: `${(new Date().getHours() * 60 + new Date().getMinutes()) * (60 / 60)}px`,
              zIndex: 1,
              pointerEvents: 'none'
            }}
          >
            <Box
              sx={{
                height: '2px',
                background: 'linear-gradient(90deg, #00BCD4 0%, #20B2AA 100%)',
                position: 'relative'
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: '80px',
                  top: '-4px',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#00BCD4'
                }}
              />
            </Box>
          </Box>
        )}
      </Box>
    </StyledCalendarWrapper>
  );
};

export default EventCalendarView;